import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Minus, Plus, RotateCcw, X } from 'lucide-react';
import { useDrawer } from '@/app/providers';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { HEADER_ICON_BUTTON } from '@/shared/ui-class';
import {
  EXPAND_ZOOM_IDENTITY,
  EXPAND_ZOOM_STEP,
  panBy,
  zoomAt,
  zoomPercent,
  type ExpandZoom,
} from './ExpandView.transform';

// =========================================================================
// ExpandView — the demo's "⛶ Expand view" overlay (demo lines 1913–1932).
// Fork-owned (zone C).
//
// Shows the current page full-screen with zoom and pan, for a projector or a
// dense chart. The demo cloned the DOM node; this renders the route's element
// a second time inside the overlay, so every control in it still works —
// figures open their source drawer, tabs switch, links navigate (and the
// overlay follows the page it lands on).
//
// - Zoom: the − / + buttons, the mouse wheel (×1.12 a notch, 40 %–400 %,
//   anchored on the pointer), or + / − / 0 on the keyboard.
// - Pan: drag anywhere that isn't a control or a table, or the arrow keys.
// - Close: the ✕ button or Esc. Esc with a drawer open closes the drawer
//   first, as it would anywhere else.
//
// Sits at z-70, under the drawer (80/81) and the toast (300), so a drawer
// opened from inside the overlay lands on top of it.
// =========================================================================

/** Pixels an arrow key pans. */
const KEY_PAN = 60;

/** Pixels the pointer must travel before a press becomes a drag. */
const DRAG_THRESHOLD = 4;

/** Where a drag must not start — the demo's `button,a,input,select,table,.twrap`. */
const NO_DRAG = 'button,a,input,select,textarea,label,table,[role="tab"],[contenteditable="true"]';

export type ExpandViewProps = {
  open: boolean;
  /** Overlay heading — the page's own name. */
  title: string;
  onClose: () => void;
  children: ReactNode;
};

export function ExpandView({ open, title, onClose, children }: ExpandViewProps) {
  const [zoom, setZoom] = useState<ExpandZoom>(EXPAND_ZOOM_IDENTITY);
  const [dragging, setDragging] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const { stack } = useDrawer();
  const drawerOpen = stack.length > 0;

  /** Zoom about the top centre of the viewport — the buttons and keys. */
  const zoomCentre = useCallback((factor: number) => {
    const width = bodyRef.current?.clientWidth ?? 0;
    setZoom((z) => zoomAt(z, factor, width / 2, 0));
  }, []);

  const reset = useCallback(() => setZoom(EXPAND_ZOOM_IDENTITY), []);

  // Each opening starts at 100 %, and focus moves into the overlay and back.
  useEffect(() => {
    if (!open) return;
    setZoom(EXPAND_ZOOM_IDENTITY);
    const previous = document.activeElement;
    closeRef.current?.focus();
    return () => {
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // A drawer or the ⌘K palette opened over the overlay owns the keyboard.
      const focus = document.activeElement;
      const elsewhere =
        focus instanceof HTMLElement &&
        focus !== document.body &&
        !rootRef.current?.contains(focus);
      if (drawerOpen || elsewhere) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      const target = e.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName))
      ) {
        return;
      }
      const pan: Record<string, [number, number]> = {
        ArrowLeft: [KEY_PAN, 0],
        ArrowRight: [-KEY_PAN, 0],
        ArrowUp: [0, KEY_PAN],
        ArrowDown: [0, -KEY_PAN],
      };
      if (e.key === '+' || e.key === '=') zoomCentre(EXPAND_ZOOM_STEP);
      else if (e.key === '-') zoomCentre(1 / EXPAND_ZOOM_STEP);
      else if (e.key === '0') reset();
      else if (pan[e.key]) setZoom((z) => panBy(z, ...pan[e.key]));
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, drawerOpen, onClose, zoomCentre, reset]);

  // Wheel zoom needs a non-passive listener to stop the page scrolling.
  useEffect(() => {
    const body = bodyRef.current;
    if (!open || !body) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = body.getBoundingClientRect();
      const factor = e.deltaY < 0 ? EXPAND_ZOOM_STEP : 1 / EXPAND_ZOOM_STEP;
      setZoom((z) => zoomAt(z, factor, e.clientX - rect.left, e.clientY - rect.top));
    };
    body.addEventListener('wheel', onWheel, { passive: false });
    return () => body.removeEventListener('wheel', onWheel);
  }, [open]);

  if (!open) return null;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest(NO_DRAG)) return;
    drag.current = { x: e.clientX, y: e.clientY, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const from = drag.current;
    if (!from) return;
    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;
    // Capture only once the pointer has really moved, so a plain click on a
    // clickable card still reaches the card.
    if (!from.moved) {
      if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    }
    drag.current = { x: e.clientX, y: e.clientY, moved: true };
    setZoom((z) => panBy(z, dx, dy));
  };

  const endDrag = () => {
    drag.current = null;
    setDragging(false);
  };

  const overlay = (
    <Commentable name="OemExpandView">
      <div
        ref={rootRef}
        className="fixed inset-0 z-[70] flex flex-col bg-[var(--background)]"
        role="dialog"
        aria-modal="true"
        aria-label={`${title} — expanded view`}
      >
        <div
          className="flex h-12 flex-shrink-0 items-center gap-2 px-4"
          style={{ borderBottom: '1px solid var(--hairline)' }}
        >
          <h2
            className="min-w-0 flex-1 truncate text-sm font-semibold"
            style={{ color: 'var(--ink-primary)' }}
          >
            {title}
          </h2>
          <span className="hidden text-xs sm:inline" style={{ color: 'var(--ink-tertiary)' }}>
            Drag to pan · wheel to zoom · Esc to close
          </span>
          <div className="flex items-center gap-0.5" role="group" aria-label="Zoom">
            <button
              type="button"
              className={HEADER_ICON_BUTTON}
              onClick={() => zoomCentre(1 / EXPAND_ZOOM_STEP)}
              aria-label="Zoom out"
              title="Zoom out (−)"
            >
              <Minus className="h-4 w-4" strokeWidth={1.6} aria-hidden="true" />
            </button>
            <output
              className="mono w-12 text-center text-xs tabular-nums"
              style={{ color: 'var(--ink-secondary)' }}
              aria-live="polite"
            >
              {zoomPercent(zoom)}
            </output>
            <button
              type="button"
              className={HEADER_ICON_BUTTON}
              onClick={() => zoomCentre(EXPAND_ZOOM_STEP)}
              aria-label="Zoom in"
              title="Zoom in (+)"
            >
              <Plus className="h-4 w-4" strokeWidth={1.6} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={HEADER_ICON_BUTTON}
              onClick={reset}
              aria-label="Reset zoom and position"
              title="Reset (0)"
            >
              <RotateCcw className="h-4 w-4" strokeWidth={1.6} aria-hidden="true" />
            </button>
          </div>
          <button
            ref={closeRef}
            type="button"
            className={HEADER_ICON_BUTTON}
            onClick={onClose}
            aria-label="Close expanded view"
            title="Close (Esc)"
          >
            <X className="h-4 w-4" strokeWidth={1.6} aria-hidden="true" />
          </button>
        </div>

        <div
          ref={bodyRef}
          className={cn(
            'relative min-h-0 flex-1 touch-none overflow-hidden',
            dragging ? 'cursor-grabbing select-none' : 'cursor-grab',
          )}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div
            className="w-full origin-top-left"
            style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.k})` }}
          >
            {children}
          </div>
        </div>
      </div>
    </Commentable>
  );

  return createPortal(overlay, document.body);
}
